import { take, fork, cancel, call, put, select, takeEvery } from 'redux-saga/effects';
import axios from '../axiosConfig';
import {
  searchSuccess,
  searchFail,
  setSelectedSearchMode,
  tourSearchRequest,
  setTourTripSearchParam, // TODO: tourSearchRequest
} from '../../slices/tripSearch';
import {
  clearTourResults,
  setIsUserInteractedWithTourList,
  setTourCatalogLoad,
  tourRemainingSearchRequest,
  tourRemainingSearchStop,
  tourSearchFail,
  tourSearchSuccess,
  updateTourSearchObj,
} from '../../slices/tour';
import { SearchModes, SearchStatus, SortDirection, TourSortingParams } from '../../../constants';
import tourSearchInput from '../../../helpers/selectors/tourSearchInput';
import generateUniqueId from '../../../utils/generateUniqueId';

const queryString = require('query-string');

// searchObj holds what was submitted; searchInputs is the form, written on every pick before Search.
function* selectTourSearchParams() {
  const key = 'tour';
  const {
    startDate,
    endDate,
    country,
    region,
    town,
    text,
    page,
    pageSize,
    types,
    activities,
    difficulties,
    languages,
    features,
    minPrice,
    maxPrice,
    minDurationMinutes,
    maxDurationMinutes,
    earliestStartTime,
    finishesBy,
    sortBy,
    sortDirection,
  } = yield select((store) => store.tripSearch.searchObj[key]);
  const searchParams = {
    text,
    country: text ? undefined : country,
    region: text ? undefined : region,
    town: text ? undefined : town,
    startDate,
    endDate,
    types,
    activities,
    difficulties,
    languages,
    minPrice,
    maxPrice,
    minDurationMinutes,
    maxDurationMinutes,
    earliestStartTime,
    finishesBy,
    sortBy: TourSortingParams[sortBy],
    sortOrder: sortDirection === SortDirection.DESCEND ? 'desc' : undefined,
    page,
    pageSize,
  };
  (features || []).forEach((feature) => {
    searchParams[feature] = true;
  });
  return searchParams;
}

function* saveTourCatalogLoad(payload, load) {
  if (payload.isTripSearch) {
    yield put(setTourTripSearchParam(load));
  } else {
    yield put(setTourCatalogLoad({ tripId: payload.tripId, ...load }));
  }
}

const buildCatalogKey = (searchParams) =>
  [searchParams.text, searchParams.country, searchParams.region, searchParams.town].join('|');

// tourRemainingSearchStop is the one way to end a load: the middleware closes its socket, this clears
// its state. TourInformation dispatches it on unmount as well.
function* stopCatalogLoad(payload) {
  yield put(
    tourRemainingSearchStop({ tripId: payload.tripId, isTripSearch: payload.isTripSearch }),
  );
}

function* stopCatalogLoadWorker({ payload }) {
  yield call(saveTourCatalogLoad, payload, {
    requestId: undefined,
    catalogKey: undefined,
    searchProgress: undefined,
  });
}

function* stopOtherCatalogLoad(payload, searchParams) {
  const { requestId, catalogKey: loadingCatalogKey } = yield select(
    tourSearchInput(payload.tripId, payload.isTripSearch),
  );
  if (!requestId || loadingCatalogKey === buildCatalogKey(searchParams)) {
    return;
  }

  yield call(stopCatalogLoad, payload);
}

// The main page shares one result list across the header modes. A catalogue load started under Tours
// keeps sending frames after the mode changes, and each frame would write tour cards over the cruise or
// hotel results, so the load stops as soon as the mode is no longer Tours.
function* stopMainPageTourLoadWorker({ payload: mode }) {
  if (mode === SearchModes.TOUR) {
    return;
  }

  const { requestId } = yield select(tourSearchInput(undefined, true));
  if (!requestId) {
    return;
  }

  yield call(stopCatalogLoad, { isTripSearch: true });
}

function* startRemainingToursLoad(payload, searchParams) {
  const catalogKey = buildCatalogKey(searchParams);
  const {
    searchProgress,
    catalogKey: loadingCatalogKey,
    requestId: previousRequestId,
  } = yield select(tourSearchInput(payload.tripId, payload.isTripSearch));
  const isLoadingThisCatalog =
    searchProgress !== undefined && searchProgress !== null && loadingCatalogKey === catalogKey;
  if (isLoadingThisCatalog) {
    return;
  }

  const requestId = generateUniqueId();
  yield call(saveTourCatalogLoad, payload, {
    requestId,
    catalogKey,
    searchProgress: 10,
    foundTotalTours: undefined,
  });
  yield put(
    tourRemainingSearchRequest({
      tripId: payload.tripId,
      isTripSearch: payload.isTripSearch,
      requestId,
      previousRequestId,
    }),
  );
}

function* tourSearchWorker({ payload }) {
  if (!payload.isTripSearch && !payload.tripId) {
    return;
  }

  try {
    const selectedSearchMode = yield select((store) => store.tripSearch.selectedSearchMode);
    if (payload.isTripSearch && selectedSearchMode !== SearchModes.TOUR) {
      return;
    }

    const searchParams = yield call(selectTourSearchParams);
    if (!payload.isLoadingMore) {
      yield call(stopOtherCatalogLoad, payload, searchParams);
      yield put(setIsUserInteractedWithTourList(false));
      yield put(clearTourResults({ tripId: payload.tripId }));
      if (payload.isTripSearch) {
        yield put(
          setTourTripSearchParam({
            ...payload,
            searchStatus: SearchStatus.SEARCHING,
          }),
        );
      } else {
        yield put(
          updateTourSearchObj({
            ...payload,
            searchStatus: SearchStatus.SEARCHING,
          }),
        );
      }
    }
    const qs = queryString.stringify(searchParams, { skipNull: true });
    const url = `/tours/search?${qs}`;
    const config = {
      method: 'get',
      url,
      // A background refresh is not retried on a 429 or a 5xx: the next frame brings another one.
      'axios-retry': payload.isLoadingMore ? { retries: 0 } : undefined,
    };
    const { data } = yield call(axios, config);
    if (payload.isTripSearch) {
      yield put(searchSuccess(data));
    } else {
      yield put(tourSearchSuccess({ tripId: payload.tripId, response: data }));
    }
    yield call(saveTourCatalogLoad, payload, { loadedToursCount: data.loadedToursCount });
    if (!payload.isLoadingMore && data.doHaveMoreTours) {
      yield call(startRemainingToursLoad, payload, searchParams);
    }
  } catch (error) {
    // A failed background refresh keeps the list that is on screen.
    if (payload.isLoadingMore) {
      return;
    }

    if (payload.isTripSearch) {
      yield put(searchFail(`${error}`));
    } else {
      yield put(tourSearchFail({ tripId: payload.tripId, error: `${error}` }));
    }
  }
}

function* tourRemainingSearchWorker({ payload }) {
  try {
    const { country, region, town, text } = yield select(
      (store) => store.tripSearch.searchObj.tour,
    );
    const qs = queryString.stringify(
      {
        text,
        country: text ? undefined : country,
        region: text ? undefined : region,
        town: text ? undefined : town,
        requestId: payload.requestId,
        tripId: payload.tripId,
      },
      { skipNull: true },
    );
    // The answer is the end of the load, whether or not the 100% frame reached the socket. Not retried:
    // a repeat would start another server load under the same requestId.
    const { data } = yield call(axios, {
      method: 'get',
      url: `/tours/search/remaining?${qs}`,
      'axios-retry': { retries: 0 },
    });
    const current = yield select(tourSearchInput(payload.tripId, payload.isTripSearch));
    if (current.requestId !== payload.requestId) {
      return;
    }

    yield call(saveTourCatalogLoad, payload, {
      searchProgress: undefined,
      foundTotalTours: data.toursCount,
    });
    yield put(
      tourRemainingSearchStop({ tripId: payload.tripId, isTripSearch: payload.isTripSearch }),
    );
    const hasUserInteractedWithTourList = yield select(
      (store) => store.tour.hasUserInteractedWithTourList,
    );
    if (!hasUserInteractedWithTourList) {
      yield put(
        tourSearchRequest({
          tripId: payload.tripId,
          isTripSearch: payload.isTripSearch,
          isLoadingMore: true,
        }),
      );
    }
  } catch {
    const current = yield select(tourSearchInput(payload.tripId, payload.isTripSearch));
    if (current.requestId !== payload.requestId) {
      return;
    }

    yield call(stopCatalogLoad, payload);
  }
}

// Two trips can be open at once and the main page searches on its own, so a request may only supersede
// an earlier one for the same place. Without that, a slow first answer lands after a quick second one
// and the results on screen stop matching the filters that produced them.
function* watchPerSearch(isMatch, run) {
  const running = {};

  while (true) {
    const action = yield take(isMatch);
    const key = action.payload?.isTripSearch ? 'tripSearch' : action.payload?.tripId;
    if (running[key]) {
      yield cancel(running[key]);
    }
    running[key] = yield fork(run, action);
  }
}

function* tourSearchWatcher() {
  yield fork(
    watchPerSearch,
    (action) => action.type === tourSearchRequest.toString(),
    tourSearchWorker,
  );
  yield takeEvery(tourRemainingSearchRequest.toString(), tourRemainingSearchWorker);
  yield takeEvery(setSelectedSearchMode.toString(), stopMainPageTourLoadWorker);
  yield takeEvery(tourRemainingSearchStop.toString(), stopCatalogLoadWorker);
}

export default tourSearchWatcher;
